<template>
  <div class="fai-container" dir="rtl">
    <!-- Breadcrumb -->
    <nav class="breadcrumb">
      <ul>
        <li>
          <router-link to="/sales/home">{{ $t('sales') }}</router-link>
        </li>
        <li>/</li>
        <li>{{ $t('faiSalesInvoices') }}</li>
      </ul>
    </nav>

    <!-- Main Content -->
    <div class="card">
      <!-- Header -->
      <h2 class="card-title">{{ $t('faiSalesInvoices') }}</h2>

      <!-- Date Filters -->
      <div class="filters">
        <div class="filter-group">
          <label for="startDate">{{ $t('from') }}</label>
          <input
            id="startDate"
            type="date"
            v-model="startDate"
            :placeholder="$t('selectDate')"
            @change="validateDates"
            aria-label="تاريخ البداية"
          />
          <span v-if="dateError" class="error">{{ dateError }}</span>
        </div>
        <div class="filter-group">
          <label for="endDate">{{ $t('to') }}</label>
          <input
            id="endDate"
            type="date"
            v-model="endDate"
            :placeholder="$t('selectDate')"
            @change="validateDates"
            aria-label="تاريخ النهاية"
          />
        </div>
        <div class="filter-buttons">
          <button
            class="btn btn-primary"
            @click="fetchInvoices"
            :disabled="loading || dateError"
            aria-label="بحث"
          >
            {{ $t('search') }}
          </button>
          <button
            class="btn btn-secondary"
            @click="clearFilters"
            aria-label="مسح الفلاتر"
          >
            {{ $t('clear') }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
      </div>

      <!-- No Data State -->
      <div v-else-if="!invoices.length && !loading" class="no-data">
        {{ $t('noInvoices') }}
      </div>

      <!-- Data Table -->
      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th @click="sort('id')" :class="{ sortable: true, active: sortKey === 'id' }">
                {{ $t('invoiceId') }}
                <span class="sort-icon">{{ getSortIcon('id') }}</span>
              </th>
              <th>{{ $t('invnum') }}</th>
              <th @click="sort('invoiceDate')" :class="{ sortable: true, active: sortKey === 'invoiceDate' }">
                {{ $t('invoiceDate') }}
                <span class="sort-icon">{{ getSortIcon('invoiceDate') }}</span>
              </th>
              <th>{{ $t('customerName') }}</th>
                                          <th>{{ $t('الرقم الضريبى للعميل') }}</th>

              <th @click="sort('amount')" :class="{ sortable: true, active: sortKey === 'amount' }">
                {{ $t('amountBeforeVat') }}
                <span class="sort-icon">{{ getSortIcon('amount') }}</span>
              </th>
              <th @click="sort('calculatedVat')" :class="{ sortable: true, active: sortKey === 'calculatedVat' }">
                {{ $t('vat') }}
                <span class="sort-icon">{{ getSortIcon('calculatedVat') }}</span>
              </th>
              <th @click="sort('totalAmountWithVat')" :class="{ sortable: true, active: sortKey === 'totalAmountWithVat' }">
                {{ $t('totalAmountWithVat') }}
                <span class="sort-icon">{{ getSortIcon('totalAmountWithVat') }}</span>
              </th>
              <th>{{ $t('status') }}</th>
              <th>{{ $t('downloadPdf') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in paginatedInvoices" :key="invoice.id">
              <td>{{ invoice.id }}</td>
              <td>{{ invoice.invnum }}</td>
              <td>{{ invoice.invoiceDate }}</td>
              <td>{{ invoice.customerName }}</td>
                                          <td>{{ invoice.customerTaxNumber }}</td>

              <td>{{ invoice.amount.toFixed(2) }} ر.س</td>
              <td>{{ invoice.calculatedVat.toFixed(2) }} ر.س</td>
              <td>{{ invoice.totalAmountWithVat.toFixed(2) }} ر.س</td>
              <td>
                <span :class="{ 'status-active': invoice.status === 'Active', 'status-inactive': invoice.status !== 'Active' }">
                  {{ invoice.status }}
                </span>
              </td>
              <td>
                <a
                  :href="invoice.pdfUrl"
                  target="_blank"
                  class="link"
                  :aria-label="`تحميل PDF لفاتورة ${invoice.invnum}`"
                >
                  ⬇ {{ $t('downloadPdf') }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Summary Section -->
        <div class="summary">
          <h3 class="summary-title">{{ $t('summary') }}</h3>
          <div class="summary-items">
            <div class="summary-item">
              <span class="summary-label">{{ $t('totalAmountBeforeVat') }}</span>
              <span class="summary-value" aria-label="إجمالي المبلغ قبل الضريبة">
                {{ totalAmountBeforeVat.toFixed(2) }} ر.س
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ $t('totalVat') }}</span>
              <span class="summary-value" aria-label="إجمالي الضريبة">
                {{ totalVat.toFixed(2) }} ر.س
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ $t('totalAmountWithVat') }}</span>
              <span class="summary-value" aria-label="إجمالي المبلغ شامل الضريبة">
                {{ totalAmountWithVat.toFixed(2) }} ر.س
              </span>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination">
          <span>
            {{ $t('showing') }} {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, invoices.length) }} {{ $t('of') }} {{ invoices.length }}
          </span>
          <div class="pagination-buttons">
            <button
              class="btn btn-secondary"
              :disabled="currentPage === 1"
              @click="currentPage--"
              aria-label="الصفحة السابقة"
            >
              {{ $t('previous') }}
            </button>
            <button
              class="btn btn-secondary"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
              aria-label="الصفحة التالية"
            >
              {{ $t('next') }}
            </button>
          </div>
        </div>

        <!-- Download Button -->
        <div class="download">
          <button
            class="btn btn-primary"
            @click="downloadExcel"
            :disabled="loading"
            aria-label="تحميل ملف إكسل"
          >
            ⬇ {{ $t('downloadExcel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import store from '@/store';

export default {
  data() {
    return {
      startDate: '',
      endDate: '',
      invoices: [],
      excelFilePath: '',
      loading: false,
      faiCompanyId: store.state.fai_company_id || '43',
      dateError: '',
      sortKey: '',
      sortOrder: 'asc',
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    sortedInvoices() {
      if (!this.sortKey) return this.invoices;
      return [...this.invoices].sort((a, b) => {
        let aValue = a[this.sortKey];
        let bValue = b[this.sortKey];
        if (this.sortKey === 'invoiceDate') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        if (aValue < bValue) return this.sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    paginatedInvoices() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.sortedInvoices.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.invoices.length / this.perPage);
    },
    totalAmountBeforeVat() {
      return this.invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
    },
    totalVat() {
      return this.invoices.reduce((sum, invoice) => sum + invoice.calculatedVat, 0);
    },
    totalAmountWithVat() {
      return this.invoices.reduce((sum, invoice) => sum + invoice.totalAmountWithVat, 0);
    },
  },
  methods: {
    validateDates() {
      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate);
        const end = new Date(this.endDate);
        if (start > end) {
          this.dateError = this.$t('dateError');
          return;
        }
      }
      this.dateError = '';
      if (this.startDate && this.endDate) {
        this.fetchInvoices();
      }
    },
    async fetchInvoices() {
      if (!this.startDate || !this.endDate || this.dateError) return;

      this.loading = true;
      try {
        console.log('Fetching invoices from:', store.state );
        const response = await axios.post('/data/export-sales-invoices', {
          faiCompanyId: this.faiCompanyId,
          startDate: this.startDate,
          endDate: this.endDate,
          perPage: 50,
        });

        if (response.data.status === 'success') {
          this.invoices = response.data.data.tableData.map(invoice => ({
            ...invoice,
            amount: invoice.amount || invoice.totalAmountWithVat / 1.15, // Estimate 15% VAT
            calculatedVat: invoice.calculatedVat || (invoice.totalAmountWithVat - (invoice.totalAmountWithVat / 1.15)),
            pdfUrl: `https://portal.ahad.ws/api/v2/invoices/${invoice.id}/long/ar/invoice.pdf`, // Add PDF URL
          }));
          this.excelFilePath = response.data.data.excelFilePath;
        } else {
          this.invoices = [];
          this.excelFilePath = '';
          console.error(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        this.invoices = [];
        this.excelFilePath = '';
      } finally {
        this.loading = false;
        this.currentPage = 1;
      }
    },
    async downloadExcel() {
      try {
        this.loading = true;
        const response = await axios.post(
          '/data/export-sales-invoices?download=true',
          {
            faiCompanyId: this.faiCompanyId,
            startDate: this.startDate,
            endDate: this.endDate,
            perPage: 50,
          },
          { responseType: 'blob' }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `فواتير_المبيعات_${this.startDate}_${this.endDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading Excel:', error);
      } finally {
        this.loading = false;
      }
    },
    sort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return '↕';
      return this.sortOrder === 'asc' ? '↑' : '↓';
    },
    clearFilters() {
      this.startDate = '';
      this.endDate = '';
      this.invoices = [];
      this.excelFilePath = '';
      this.dateError = '';
      this.currentPage = 1;
    },
  },
};
</script>

<style>
.fai-container {
  font-family: Arial, sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
  direction: rtl;
  text-align: right;
}

.breadcrumb ul {
  list-style: none;
  padding: 0;
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
}

.breadcrumb li {
  color: #666;
}

.breadcrumb a {
  color: #2563eb;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 24px;
  color: #333;
  margin-bottom: 16px;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.filter-group {
  flex: 1;
  min-width: 200px;
}

.filter-group label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.filter-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.filter-group input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.filter-buttons {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-secondary {
  background: #e5e7eb;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  background: #d1d5db;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.no-data {
  text-align: center;
  padding: 32px;
  color: #666;
  font-size: 16px;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
}

.data-table th {
  background: #f3f4f6;
  color: #333;
  font-weight: 600;
}

.data-table th.sortable {
  cursor: pointer;
}

.data-table th.sortable:hover,
.data-table th.active {
  background: #e5e7eb;
}

.sort-icon {
  margin-left: 4px;
  font-size: 12px;
}

.data-table tr:hover {
  background: #f9fafb;
}

.status-active {
  background: #dcfce7;
  color: #15803d;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.status-inactive {
  background: #f3f4f6;
  color: #333;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.link {
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
}

.link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.summary {
  margin-top: 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.summary-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 12px;
}

.summary-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.summary-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.summary-label {
  font-size: 14px;
  color: #666;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  font-size: 14px;
  color: #666;
}

.pagination-buttons {
  display: flex;
  gap: 8px;
}

.download {
  text-align: center;
  margin-top: 24px;
}

@media (max-width: 768px) {
  .filters {
    flex-direction: column;
  }

  .filter-group {
    width: 100%;
  }

  .filter-buttons {
    width: 100%;
    justify-content: flex-end;
  }

  .data-table th,
  .data-table td {
    font-size: 12px;
    padding: 8px;
  }

  .summary-items {
    flex-direction: column;
  }

  .summary-item {
    width: 100%;
  }
}
</style>
